/**
 * Shared address autocomplete — Photon (Komoot) backed.
 * Photon returns POIs (hospitals, dialysis centers, clinics, businesses)
 * in addition to street addresses, which is what dispatch needs.
 *
 * Used by both the patient booking form (`/patient`) and the dispatcher
 * New Ride Modal so any tuning made here (biasing, ranking, POI filter)
 * benefits both surfaces.
 */
import { useEffect, useRef, useState } from "react";
import { MapPin, Building2, Home } from "lucide-react";

// Bias results to metro Atlanta (Georgia primary service area) for better
// ranking of local facilities. Photon accepts `lat` + `lon` + `location_bias_scale`.
const BIAS_LAT = 33.749;
const BIAS_LNG = -84.388;
const BIAS_SCALE = 0.5; // 0=none, 1=heavy — 0.5 balances local vs. accuracy

function labelFor(feature) {
  const p = feature.properties || {};
  const parts = [];
  if (p.name && p.name !== p.street) parts.push(p.name);
  const street = p.housenumber ? `${p.housenumber} ${p.street || ""}`.trim() : (p.street || "");
  if (street) parts.push(street);
  const locality = p.city || p.town || p.village;
  if (locality) parts.push(locality);
  if (p.state) parts.push(p.state);
  if (p.postcode) parts.push(p.postcode);
  return parts.join(", ");
}

function IconFor({ feature }) {
  const p = feature.properties || {};
  const t = (p.osm_key || "") + ":" + (p.osm_value || "");
  const isPoi = p.osm_key === "amenity" || p.osm_key === "healthcare" ||
                 p.osm_key === "building" && p.osm_value !== "residential";
  const isHome = t === "building:residential" || t === "building:house" || !!p.housenumber && !p.name;
  if (isPoi) return <Building2 className="w-3.5 h-3.5 text-emerald-700" />;
  if (isHome) return <Home className="w-3.5 h-3.5 text-brand-muted" />;
  return <MapPin className="w-3.5 h-3.5 text-brand-muted" />;
}

export function AddressAutocomplete({
  value, onChange, placeholder, testid, className, poisFirst = true,
  minChars = 3, autoFocus = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const abortRef = useRef(null);

  const query = async (q) => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=8&lang=en&lat=${BIAS_LAT}&lon=${BIAS_LNG}&location_bias_scale=${BIAS_SCALE}`;
      const r = await fetch(url, { signal: ctrl.signal });
      if (!r.ok) throw new Error(String(r.status));
      const data = await r.json();
      let features = data.features || [];
      // Prefer POIs (hospitals, dialysis centers, medical facilities) at the top when the
      // user query looks like a place name (contains letters, spaces, no long digit street numbers).
      if (poisFirst) {
        const isFacilityQuery = /(hospital|clinic|dialysis|medical|health|care|center|centre|nursing|rehab|therapy|doctor|dr\.|pharmacy|urgent)/i.test(q);
        features = features.slice().sort((a, b) => {
          const pa = a.properties || {}; const pb = b.properties || {};
          const aIsPoi = pa.osm_key === "amenity" || pa.osm_key === "healthcare";
          const bIsPoi = pb.osm_key === "amenity" || pb.osm_key === "healthcare";
          if (isFacilityQuery) return (bIsPoi ? 1 : 0) - (aIsPoi ? 1 : 0);
          return 0;
        });
      }
      setSuggestions(features);
      setOpen(true);
    } catch (e) {
      if (e.name !== "AbortError") console.warn("autocomplete failed", e);
    } finally { setLoading(false); }
  };

  const handleChange = (v) => {
    onChange(v);
    if (timer.current) clearTimeout(timer.current);
    if (v.trim().length < minChars) { setSuggestions([]); setOpen(false); return; }
    timer.current = setTimeout(() => query(v), 250);
  };

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    if (abortRef.current) abortRef.current.abort();
  }, []);

  return (
    <div className="relative">
      <input
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => value.trim().length >= minChars && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder={placeholder}
        data-testid={testid}
        autoFocus={autoFocus}
        autoComplete="off"
        className={className || "w-full px-4 py-3 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green"}
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-brand-navy/15 rounded-lg max-h-72 overflow-auto shadow-xl"
              data-testid={testid ? `${testid}-suggestions` : undefined}>
          {suggestions.map((s, i) => {
            const label = labelFor(s);
            const coords = s.geometry?.coordinates;
            return (
              <button
                key={i}
                type="button"
                onMouseDown={(e) => e.preventDefault()}  /* keep focus so onBlur doesn't fire */
                onClick={() => {
                  onChange(label, coords ? { lng: coords[0], lat: coords[1] } : undefined, s.properties || {});
                  setOpen(false);
                  setSuggestions([]);
                }}
                className="w-full text-left px-3 py-2 text-sm text-brand-deep hover:bg-brand-surfaceAlt border-b border-brand-navy/5 last:border-b-0 flex items-start gap-2"
                data-testid={testid ? `${testid}-suggestion-${i}` : undefined}
              >
                <span className="mt-1"><IconFor feature={s} /></span>
                <span className="flex-1 min-w-0">{label}</span>
              </button>
            );
          })}
        </div>
      )}
      {loading && open && suggestions.length === 0 && value.trim().length >= minChars && (
        <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-brand-navy/15 rounded-lg shadow px-3 py-2 text-xs text-brand-muted">
          Looking up "{value}"…
        </div>
      )}
    </div>
  );
}
